import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  Avatar,
  Alert
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useMasterPlayer } from '../../context/MasterPlayerContext';
import { usePlayer } from '../../context/PlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import './ImportMasterPlayersDialog.scss';

const ImportMasterPlayersDialog = ({ open, onClose }) => {
  const { masterPlayers } = useMasterPlayer();
  const { players, addPlayer } = usePlayer();
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [importing, setImporting] = useState(false);

  // Filter out players that already exist in the current session
  const existingPlayerNames = players.map(p => p.name.toLowerCase());
  const availablePlayers = masterPlayers.filter(
    masterPlayer => !existingPlayerNames.includes(masterPlayer.name.toLowerCase())
  );

  const handlePlayerToggle = (player, event) => {
    // Prevent double firing when clicking checkbox
    if (event) {
      event.stopPropagation();
    }
    
    setSelectedPlayers(prev => {
      const isSelected = prev.some(p => p.id === player.id);
      if (isSelected) {
        return prev.filter(p => p.id !== player.id);
      } else {
        return [...prev, player];
      }
    });
  };

  const handleCheckboxChange = (player, event) => {
    event.stopPropagation();
    handlePlayerToggle(player);
  };

  const handleImport = async () => {
    if (selectedPlayers.length === 0) return;

    setImporting(true);
    try {
      // Import selected players with default $200 buy-in
      for (const masterPlayer of selectedPlayers) {
        await addPlayer(masterPlayer.name, {
          amount: 200,
          note: 'Default buy-in from import'
        });
      }
      
      setSelectedPlayers([]);
      onClose();
    } catch (error) {
      console.error('Error importing players:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSelectedPlayers([]);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className="import-master-players-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className="dialog-title">
        <Box className="title-content">
          <GroupAddIcon className="title-icon" />
          <Box>
            <Typography variant="h5">
              Import from Master List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add players from your master list to this session with a default $200 buy-in
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent className="dialog-content">
        {availablePlayers.length === 0 ? (
          <Alert severity="info" className="info-alert">
            {masterPlayers.length === 0 
              ? 'No players in your master list yet. Add some players to your master list first.'
              : 'All master list players are already in this session.'
            }
          </Alert>
        ) : (
          <Box className="player-selection">
            <Typography variant="body2" color="text.secondary" className="selection-help">
              Select players to import into this session (each will start with a $200 buy-in):
            </Typography>
            
            <List className="player-list">
              {availablePlayers.map((player) => {
                const isSelected = selectedPlayers.some(p => p.id === player.id);
                return (
                  <ListItem 
                    key={player.id} 
                    className={`player-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handlePlayerToggle(player)}
                    button
                    disableRipple
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={(event) => handleCheckboxChange(player, event)}
                      className="player-checkbox"
                      color="primary"
                    />
                  <ListItemAvatar>
                    <Avatar
                      className="player-avatar"
                      style={{
                        backgroundColor: player.profileColor,
                        color: getContrastTextColor(player.profileColor)
                      }}
                    >
                      {getInitials(player.name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" className="player-name">
                        {player.name}
                      </Typography>
                    }
                    secondary={
                      <Box className="player-details">
                        {player.email && (
                          <Typography variant="caption" className="player-email">
                            {player.email}
                          </Typography>
                        )}
                        {player.notes && (
                          <Typography variant="caption" className="player-notes">
                            {player.notes}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                );
              })}
            </List>

            {selectedPlayers.length > 0 && (
              <Alert severity="success" className="selection-info">
                {selectedPlayers.length} player{selectedPlayers.length > 1 ? 's' : ''} selected for import
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions className="dialog-actions">
        <Button 
          onClick={handleClose}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleImport}
          variant="contained"
          disabled={selectedPlayers.length === 0 || importing}
        >
          {importing ? 'Importing...' : `Import ${selectedPlayers.length > 0 ? selectedPlayers.length : ''} Player${selectedPlayers.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportMasterPlayersDialog;
